/**
 * AP-13: CustomFieldsForm — dynamisches Formular auf Basis eines
 * `CustomFieldsSchema` (siehe AP-09-C).
 *
 * Rendert pro Feldtyp das passende Eingabe-Widget:
 *   - string  → text input (mit pattern / maxLength als Hinweis)
 *   - number  → number input (min / max)
 *   - boolean → checkbox
 *   - date    → date input (yyyy-MM-dd)
 *   - enum    → select mit `options[]`
 *
 * Validierung: clientseitig minimal (required + Typ). Serverseitig validiert
 * `CustomFieldsValidator` strikt; das ist die Quelle der Wahrheit. Die UI
 * blockiert nur die offensichtlichen Fehler (leeres Pflichtfeld).
 *
 * Wert-Transport: Werte fließen als `Record<string, unknown>` rein und raus.
 * Felder, die nicht im Schema definiert sind, werden ignoriert (außer das
 * Schema setzt `strict=false` — dann werden zusätzliche Felder im Server
 * akzeptiert; der Editor zeigt sie aber nicht).
 */
import { fieldLabel, inputBase, selectBase } from '@/lib/form-styles';
import type { CustomFieldsSchema, CustomFieldSpec } from '../types/time-master-data.types';

export interface CustomFieldsFormProps {
  schema: CustomFieldsSchema | null;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  disabled?: boolean;
}

export function CustomFieldsForm({ schema, value, onChange, disabled = false }: CustomFieldsFormProps) {
  if (!schema || schema.fields.length === 0) {
    return (
      <p className="text-xs text-muted-foreground italic">
        Noch keine eigenen Felder festgelegt (siehe „Eigene Felder" unter Auswertungen &gt; Stammdaten Zeit).
      </p>
    );
  }

  const setField = (name: string, v: unknown) => {
    onChange({ ...value, [name]: v });
  };

  return (
    <div className="space-y-3">
      {schema.fields.map((f) => (
        <CustomField
          key={f.name}
          spec={f}
          value={value?.[f.name]}
          onChange={(v) => setField(f.name, v)}
          disabled={disabled}
        />
      ))}
    </div>
  );
}

interface CustomFieldProps {
  spec: CustomFieldSpec;
  value: unknown;
  onChange: (v: unknown) => void;
  disabled: boolean;
}

function CustomField({ spec, value, onChange, disabled }: CustomFieldProps) {
  const label = (
    <label className={fieldLabel}>
      {spec.label ?? spec.name}
      {spec.required && <span className="text-red-500 ml-1">*</span>}
    </label>
  );

  switch (spec.type) {
    case 'string':
      return (
        <div className="space-y-1.5">
          {label}
          <input
            type="text"
            className={inputBase}
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value || undefined)}
            disabled={disabled}
            maxLength={spec.maxLength}
            pattern={spec.pattern}
          />
          {spec.maxLength && (
            <p className="text-[10px] text-muted-foreground">max. {spec.maxLength} Zeichen</p>
          )}
        </div>
      );

    case 'number':
      return (
        <div className="space-y-1.5">
          {label}
          <input
            type="number"
            className={inputBase}
            value={value == null ? '' : String(value)}
            onChange={(e) => {
              const v = e.target.value;
              onChange(v === '' ? undefined : Number(v));
            }}
            disabled={disabled}
            min={spec.min}
            max={spec.max}
          />
        </div>
      );

    case 'boolean':
      return (
        <label className="flex items-center gap-2 select-none">
          <input
            type="checkbox"
            checked={value === true}
            onChange={(e) => onChange(e.target.checked)}
            disabled={disabled}
            className="h-4 w-4"
          />
          <span className="text-sm">
            {spec.label ?? spec.name}
            {spec.required && <span className="text-red-500 ml-1">*</span>}
          </span>
        </label>
      );

    case 'date':
      return (
        <div className="space-y-1.5">
          {label}
          <input
            type="date"
            className={inputBase}
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value || undefined)}
            disabled={disabled}
          />
        </div>
      );

    case 'enum':
      return (
        <div className="space-y-1.5">
          {label}
          <select
            className={selectBase}
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value || undefined)}
            disabled={disabled}
          >
            <option value="">— bitte wählen —</option>
            {(spec.options ?? []).map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      );

    default:
      return null;
  }
}

/**
 * Hilfsfunktion für Pre-Submit-Validierung (Pflichtfelder). Liefert
 * Fehler-Map { fieldName: 'Pflichtfeld' }. Strikte Validierung passiert
 * serverseitig — diese Funktion fängt nur den offensichtlichen Fall ab.
 */
export function validateRequired(
  schema: CustomFieldsSchema | null,
  value: Record<string, unknown>,
): Record<string, string> {
  if (!schema) return {};
  const errors: Record<string, string> = {};
  for (const f of schema.fields) {
    if (!f.required) continue;
    const v = value?.[f.name];
    const empty =
      v === undefined ||
      v === null ||
      (typeof v === 'string' && v.trim() === '');
    if (empty) errors[f.name] = 'Pflichtfeld';
  }
  return errors;
}
